import { ApolloClient, createHttpLink, from } from '@apollo/client';
import { isDev, isEqual } from '@winelidays/wld-tsx-2/utils';
import merge from 'deepmerge';
// import 'cross-fetch/polyfill';
import { ENV, TOKEN } from '@/constants/config';
import safeLog, { safeWarn } from '@/utils/safeLog';
import { ApolloLink } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import generateApolloCache from './generateApolloCache';
if (isDev(ENV)) {
    // Adds messages only in a dev environment
    loadDevMessages();
    loadErrorMessages();
}
export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';
const errorLogStyles = 'color:red;';
const warningLogStyles = 'color:orange;';
const successLogStyles = 'color:limegreen;';
const WARNING_DEADLINE = 300;
const ERROR_DEADLINE = 600;
/*************
 * Logger
 *************/
let globalCount = 1;
const logger = new ApolloLink((operation, forward) => {
    const startDate = new Date();
    const startTimestamp = startDate.getTime();
    const count = globalCount++;
    const { query, variables, operationName } = operation;
    const { definitions } = query;
    const { operation: operationType } = definitions[0];
    const operationTypeAndName = `${operationType} ${operationName}`;
    /**************
     * Logs
     */
    safeLog(`%c GO #${count} ${operationTypeAndName}`, successLogStyles, variables);
    safeLog(`*****`);
    /**************
     * Forward
     */
    return forward(operation).map((result) => {
        const endTimestamp = new Date().getTime();
        const ellapsed = endTimestamp - startTimestamp;
        /**************
         * Error case
         */
        if (!result || result.errors) {
            /**************
             * Logs
             */
            safeWarn(`%c #${count} ${operationTypeAndName} (${ellapsed}ms)\n`, errorLogStyles);
            safeLog(`%c #${count}  ${operationTypeAndName} variables:\n`, errorLogStyles, variables);
            safeLog(`%c #${count} ${operationTypeAndName} result:\n`, errorLogStyles, result?.errors, result?.data);
            safeLog(`*****`);
        }
        else {
            /**************
             * Success case
             */
            /**************
             * Logs
             */
            const crossedBoundary = ellapsed > ERROR_DEADLINE ?
                ERROR_DEADLINE :
                ellapsed > WARNING_DEADLINE ?
                    WARNING_DEADLINE :
                    null;
            const ellapsedStyle = crossedBoundary === ERROR_DEADLINE ?
                errorLogStyles :
                crossedBoundary ?
                    warningLogStyles :
                    successLogStyles;
            safeLog(`%c #${count} ${operationTypeAndName} %c(${ellapsed}ms)`, successLogStyles, ellapsedStyle, startDate.toISOString());
            safeLog(`%c #${count} ${operationTypeAndName} variables:\n`, successLogStyles, variables);
            safeLog(`%c #${count} ${operationTypeAndName} results:\n`, successLogStyles, result.data);
            safeLog(`*****`);
        }
        return result;
    });
});
const initializeLinks = () => {
    const links = [];
    links.push(logger);
    const authLink = setContext((_, { headers }) => {
        return {
            headers: {
                ...headers,
                authorization: `Bearer ${TOKEN}`
            }
        };
    });
    const httpLink = createHttpLink({
        uri: process.env.GRAPHQL_API,
        fetch
    });
    links.push(authLink.concat(httpLink));
    return links;
};
/*************
 * Client Config & Creation
 *************/
let apolloClient;
const createApolloClient = () => {
    return new ApolloClient({
        link: from(initializeLinks()),
        defaultOptions: {
            watchQuery: {
                // doesn't seem to work https://github.com/apollographql/apollo-client/issues/9174
                fetchPolicy: 'cache-and-network'
            }
        },
        cache: generateApolloCache()
    });
};
/*************
 * Initialization
 *************/
export const initializeApollo = (initialState = null) => {
    const _apolloClient = apolloClient ?? createApolloClient();
    // If your page has Next.js data fetching methods that use Apollo Client, the initial state
    // gets hydrated here
    if (initialState) {
        // Get existing cache, loaded during client side data fetching
        const existingCache = _apolloClient.extract();
        // Merge the existing cache into data passed from getStaticProps/getServerSideProps
        const data = merge(initialState, existingCache, {
            // combine arrays using object equality (like in sets)
            arrayMerge: (destinationArray, sourceArray) => [
                ...sourceArray,
                // ...destinationArray,
                ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s)))
            ]
        });
        // Restore the cache with the merged data
        _apolloClient.cache.restore(data);
    }
    // Create the Apollo Client once in the client
    if (!apolloClient)
        apolloClient = _apolloClient;
    return _apolloClient;
};
